import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import type { ReactNode } from 'react';
import {
  PRIMARY_MAIN,
  PRIMARY_LIGHT,
  PRIMARY_DARK,
  SECONDARY_MAIN,
  SECONDARY_LIGHT,
  SECONDARY_DARK,
  CONTRAST_TEXT,
  ERROR_MAIN,
  ERROR_LIGHT,
  SUCCESS_MAIN,
  SUCCESS_LIGHT,
  WARNING_MAIN,
  FONT_FAMILY,
  FONT_SIZE_BASE,
  FONT_WEIGHT_LIGHT,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_BOLD,
  BORDER_RADIUS,
} from './GlobalStyles';

/**
 * Exploros Theme for Material UI
 * Based on styles from assets/themes/exploros/scss/_variables.scss
 */
const explorosTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: PRIMARY_MAIN,
      light: PRIMARY_LIGHT,
      dark: PRIMARY_DARK,
      contrastText: CONTRAST_TEXT,
    },
    secondary: {
      main: SECONDARY_MAIN,
      light: SECONDARY_LIGHT,
      dark: SECONDARY_DARK,
      contrastText: CONTRAST_TEXT,
    },
    error: {
      main: ERROR_MAIN,
      light: ERROR_LIGHT,
    },
    success: {
      main: SUCCESS_MAIN,
      light: SUCCESS_LIGHT,
    },
    warning: {
      main: WARNING_MAIN,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE_BASE,
    fontWeightLight: FONT_WEIGHT_LIGHT,
    fontWeightRegular: FONT_WEIGHT_REGULAR,
    fontWeightMedium: FONT_WEIGHT_MEDIUM,
    fontWeightBold: FONT_WEIGHT_BOLD,
    h1: {
      fontSize: '34px',
      fontWeight: 700,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 700,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 500,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 500,
    },
    h5: {
      fontSize: '16px',
      fontWeight: 500,
    },
    h6: {
      fontSize: '14px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '16px',
    },
    body2: {
      fontSize: '14px',
    },
    caption: {
      fontSize: '12px',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: BORDER_RADIUS,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          width: '100%',
          height: '100%',
        },
        body: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT_REGULAR,
          fontSize: '20px',
          margin: '0 auto',
          height: '100%',
          width: '100%',
        },
        button: {
          fontFamily: FONT_FAMILY,
          fontWeight: 300,
        },
        input: {
          fontFamily: FONT_FAMILY,
          fontWeight: 300,
        },
        textarea: {
          fontFamily: FONT_FAMILY,
          fontWeight: 300,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: PRIMARY_DARK,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: PRIMARY_MAIN,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY_MAIN,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY_MAIN,
          },
        },
      },
    },
  },
});

interface Props {
  children: ReactNode;
}

export function MuiProvider({ children }: Props) {
  return (
    <ThemeProvider theme={explorosTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

// Export the theme for use in other components
export { explorosTheme };
